import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useInstanceStore } from '../store/useInstanceStore';
import { ApiError } from '../lib/api';
import { currentDesktopWindowState, desktop, type FirmioDesktopApi } from '../lib/desktop';
import { feedCursorKey, feedRetryDelayMs, shouldNotify } from '../lib/desktopNotify';
import {
  FEED_WAIT_SECONDS,
  pollNotificationFeed,
  type FeedEvent,
} from '../lib/notificationFeedApi';
import { soundForPush } from '../lib/pushSounds';
import { canPlayNow, playSound } from '../lib/sounds';
import { isTestModeEnabled } from '../lib/testMode';

/**
 * System notifications in the Firmio desktop app (desktop/, Electron).
 *
 * Electron has no Web Push, so instead of a service worker this page keeps a
 * long-poll open on the notification feed (GET /api/notifications/feed). The
 * desktop window is never destroyed — closing it hides it in the tray, with
 * background throttling off — so the loop keeps running while the window is
 * out of sight, and every event becomes a system notification within a
 * second. The page plays the Firmio sound itself (like lib/pushSounds.ts for
 * web push) and marks the notification silent, so there is one sound.
 *
 * Mounted once, in App (outside Layout), so it also runs in a Meet room.
 * Inert in a browser, in Test Mode, signed out, on a suspended instance and in
 * a second desktop window (an invoice opened next to the list).
 */
export function useDesktopNotifications(): void {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const signedIn = useAuthStore((s) => s.accessToken !== null);
  const suspended = useInstanceStore((s) => s.suspended);

  // A notification click (or a firmio://open link) opens its page.
  useEffect(() => {
    const api = desktop();
    if (!api) return;
    return api.onNavigate((path) => navigate(path));
  }, [navigate]);

  useEffect(() => {
    const api = desktop();
    if (!api || !signedIn || !userId || suspended || isTestModeEnabled()) return;
    const stop = new AbortController();
    void runFeedLoop(api, userId, stop.signal);
    return () => stop.abort();
  }, [signedIn, userId, suspended]);
}

/** The tray's "Notificări" switch, kept current. */
let trayNotificationsOn = true;
let trayWired = false;

function wireTraySwitch(api: FirmioDesktopApi): void {
  if (trayWired) return;
  trayWired = true;
  void api.notificationsEnabled().then((on) => (trayNotificationsOn = on));
  api.onNotificationsEnabledChange((on) => (trayNotificationsOn = on));
}

/** The saved cursor, or null when this window has none yet. */
function readCursor(userId: string): number | null {
  try {
    const raw = sessionStorage.getItem(feedCursorKey(userId));
    const value = raw === null ? Number.NaN : Number(raw);
    return Number.isInteger(value) && value >= 0 ? value : null;
  } catch {
    return null;
  }
}

function writeCursor(userId: string, cursor: number): void {
  try {
    sessionStorage.setItem(feedCursorKey(userId), String(cursor));
  } catch {
    // Storage unavailable: the cursor stays in memory for this run.
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timer);
      resolve();
    });
  });
}

function deliver(api: FirmioDesktopApi, event: FeedEvent): void {
  if (!trayNotificationsOn) return;
  if (!shouldNotify(event.url, currentDesktopWindowState(), window.location.pathname)) return;
  const sound = soundForPush(event.sound);
  const played = sound !== null && canPlayNow();
  if (played) playSound(sound);
  api.notify({
    id: event.id,
    title: event.title,
    body: event.body,
    url: event.url,
    tag: event.tag,
    silent: played,
  });
}

async function runFeedLoop(
  api: FirmioDesktopApi,
  userId: string,
  signal: AbortSignal,
): Promise<void> {
  if (!(await api.isMainWindow()) || signal.aborted) return;
  wireTraySwitch(api);

  let cursor = readCursor(userId);
  let failures = 0;
  while (!signal.aborted) {
    try {
      // The first request (no cursor) only learns where "now" is.
      const waiting = cursor !== null;
      const started = Date.now();
      const page = await pollNotificationFeed(cursor, FEED_WAIT_SECONDS, signal);
      failures = 0;
      for (const event of page.events) deliver(api, event);
      cursor = page.cursor;
      writeCursor(userId, cursor);
      // A long-poll that came back empty at once (a proxy cutting it short)
      // must not turn into a tight loop.
      if (waiting && page.events.length === 0 && Date.now() - started < 1000) {
        await sleep(1000, signal);
      }
    } catch (err) {
      if (signal.aborted) return;
      // Suspended: the app swaps to SuspendedPage and this effect stops.
      if (err instanceof ApiError && err.code === 'INSTANCE_SUSPENDED') return;
      failures += 1;
      await sleep(feedRetryDelayMs(failures), signal);
    }
  }
}
